import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, Trash, Money, SignIn } from "@phosphor-icons/react";
import { Spinner, Pill, FlagPill, Avatar, STATUS_TONE, isHr, err } from "../components/hr/hrUtils";
import { TONE_CLASSES } from "../components/assets/assetUtils";
import { PersonalCard, AddressesCard, ContactsCard } from "../components/hr/EmployeeCards";
import { EmploymentCard, RightToWorkCard, PayrollCard } from "../components/hr/EmploymentCards";
import { DocumentsCard, NotesCard } from "../components/hr/DocumentsCards";
import { ChecklistsCard } from "../components/hr/ChecklistsCard";
import { ReviewsCard } from "../components/hr/ReviewsCard";
import { TrainingCard } from "../components/hr/TrainingCard";

const TABS = [["details", "Details"], ["employment", "Employment & pay"], ["documents", "Documents"], ["checklists", "Onboarding"], ["reviews", "Reviews"], ["training", "Training"], ["notes", "HR notes"]];

/** /hr/people/:id — one person's full HR record (HR view). The same cards power /hr/me in self-service mode. */
export default function HrEmployeePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "details";
  const setTab = (t) => setParams(t === "details" ? {} : { tab: t }, { replace: true });
  const [emp, setEmp] = useState(null);
  const [meta, setMeta] = useState(null);
  const [managers, setManagers] = useState([]);
  const hr = isHr(user);

  const load = useCallback(() => API.get(`/hr/employees/${id}`).then((r) => setEmp(r.data)).catch(async (e) => {
    if (e?.response?.status === 403) {  // a line manager asked to hold a review gets a reviews-only view
      try { const rv = await API.get(`/hr/employees/${id}/reviews`); setEmp({ id, reviewer_only: true, name: rv.data.employee_name, display_name: rv.data.employee_name }); return; } catch { /* fall through */ }
    }
    err(e, "Couldn't load that record"); setEmp(false);
  }), [id]);
  useEffect(() => { load(); API.get("/hr/meta").then((r) => setMeta(r.data)).catch(() => setMeta(null)); }, [load]);
  useEffect(() => { if (hr) API.get("/hr/employees", { params: { status: "active" } }).then((r) => setManagers(r.data.employees)).catch(() => setManagers([])); }, [hr]);

  const remove = async () => {
    if (!window.confirm(`Delete ${emp.name}'s HR record and all their documents? This cannot be undone.`)) return;
    try { const r = await API.delete(`/hr/employees/${id}`); toast.success(r.data.message); nav("/hr?tab=people"); } catch (e) { err(e, "Couldn't delete"); }
  };

  if (emp === null) return <Spinner />;
  if (emp === false) return <div className="p-6 text-sm text-muted-foreground">Record not found. <Link to="/hr?tab=people" className="text-primary underline">Back to People</Link></div>;
  if (emp.reviewer_only) return (
    <div className="p-4 sm:p-6 space-y-4 max-w-3xl" data-testid="hr-reviewer-page">
      <div className="flex items-center gap-3"><Avatar name={emp.name} size="lg" /><div><h1 className="text-xl sm:text-2xl font-bold" data-testid="hr-emp-name">{emp.display_name}</h1><p className="text-sm text-muted-foreground">Reviews you've been asked to hold — the rest of the HR record stays private.</p></div></div>
      <ReviewsCard emp={emp} hr={false} />
    </div>
  );
  const visibleTabs = TABS.filter(([k]) => hr || k !== "notes");

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="hr-employee-page">
      <div className="flex flex-wrap items-start gap-3">
        {hr && <Link to="/hr?tab=people" className="mt-2 text-muted-foreground hover:text-foreground" aria-label="Back to People" data-testid="hr-emp-back"><ArrowLeft size={18} /></Link>}
        <Avatar name={emp.name} size="lg" />
        <div className="flex-1 min-w-[240px]">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold" data-testid="hr-emp-name">{emp.display_name}</h1>
            <Pill tone={STATUS_TONE[emp.status]} testid="hr-emp-status">{emp.status_label}</Pill>
            {emp.login && <Pill tone={TONE_CLASSES.none}><SignIn size={11} className="mr-1" /> {emp.login.role.replace("_", " ")}</Pill>}
            {emp.payroll_employee_id && <Pill tone={TONE_CLASSES.none}><Money size={11} className="mr-1" /> Payroll {emp.payroll?.payroll_id || ""}</Pill>}
          </div>
          <p className="text-sm text-muted-foreground">{[emp.job_title, emp.department, emp.employee_number].filter(Boolean).join(" · ")}{emp.manager_name ? ` · reports to ${emp.manager_name}` : ""}</p>
          {emp.flags?.length > 0 && <div className="flex flex-wrap gap-1 mt-1.5" data-testid="hr-emp-flags">{emp.flags.map((f) => <FlagPill key={f.key} flag={f} />)}</div>}
        </div>
        {hr && ["super_admin", "hr_manager"].includes(user.role) && <Button variant="ghost" size="sm" className="text-red-600" onClick={remove} data-testid="hr-emp-delete"><Trash size={14} className="mr-1" /> Delete</Button>}
      </div>

      <div className="flex gap-1 border-b border-border">
        {visibleTabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === k ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`hr-emp-tab-${k}`}>{l}</button>)}
      </div>

      {tab === "details" && <div className="grid lg:grid-cols-2 gap-4"><div className="lg:col-span-2"><PersonalCard emp={emp} meta={meta} hr={hr} onSaved={setEmp} /></div><AddressesCard emp={emp} hr={hr} onSaved={setEmp} /><ContactsCard emp={emp} hr={hr} onSaved={setEmp} /></div>}
      {tab === "employment" && <div className="space-y-4"><EmploymentCard emp={emp} meta={meta} hr={hr} onSaved={setEmp} /><RightToWorkCard emp={emp} meta={meta} hr={hr} onSaved={setEmp} />{hr && <PayrollCard emp={emp} user={user} onSaved={load} />}</div>}
      {tab === "documents" && <DocumentsCard emp={emp} meta={meta} hr={hr} onSaved={load} />}
      {tab === "checklists" && <ChecklistsCard emp={emp} hr={hr} onEmployeeChange={load} />}
      {tab === "reviews" && <ReviewsCard emp={emp} hr={hr} managers={managers} onEmployeeChange={load} />}
      {tab === "training" && <TrainingCard emp={emp} hr={hr} />}
      {tab === "notes" && hr && <NotesCard emp={emp} />}
    </div>
  );
}
